import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { createSupplierThunk, updateSupplierThunk } from "../../redux/slices/supplierSlice";
import { getApiErrorMessage } from "../../utils/apiError";

const EMPTY = {
  supplierName: "", contactPersonName: "", mobileNumber: "", alternateContact: "",
  address: "", email: "", gstNumber: "", status: "Active",
};

const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const SupplierModal = ({ show, onClose, editRecord }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(editRecord ? { ...EMPTY, ...editRecord } : EMPTY);
  }, [editRecord, show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setSelect = (field) => (opt) => setForm((f) => ({ ...f, [field]: opt?.value ?? "" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplierName.trim()) {
      Swal.fire("Required", "Supplier Name is required.", "warning"); return;
    }
    setSaving(true);
    try {
      if (editRecord?.id) {
        await dispatch(updateSupplierThunk({ id: editRecord.id, data: form })).unwrap();
      } else {
        await dispatch(createSupplierThunk(form)).unwrap();
      }
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Supplier ${editRecord ? "updated" : "added"} successfully`, showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to save supplier."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit Supplier" : "Add Supplier"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Supplier Name <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.supplierName} onChange={set("supplierName")} placeholder="Company / Supplier Name" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Contact Person</label>
                    <input className="form-control" value={form.contactPersonName} onChange={set("contactPersonName")} placeholder="Contact Person Name" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Mobile</label>
                    <input className="form-control" value={form.mobileNumber} onChange={set("mobileNumber")} placeholder="Primary Mobile" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Alternate Contact</label>
                    <input className="form-control" value={form.alternateContact} onChange={set("alternateContact")} placeholder="Alternate Contact" />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Address</label>
                    <input className="form-control" value={form.address} onChange={set("address")} placeholder="Full Address" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" value={form.email} onChange={set("email")} placeholder="Email (optional)" />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">GST Number</label>
                    <input className="form-control" value={form.gstNumber} onChange={set("gstNumber")} placeholder="GST (optional)" />
                  </div>
                  <div className="col-lg-4 mb-3">
                    <label className="form-label">Status</label>
                    <Select classNamePrefix="react-select" options={statusOptions}
                      value={statusOptions.find((o) => o.value === form.status) || null}
                      onChange={setSelect("status")}
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Add Supplier"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default SupplierModal;
